import React, {ReactElement, useEffect, useState} from 'react';
import {
  DialogLegacy as Dialog,
  DialogLegacyContent as DialogContent,
  DialogLegacyActions as DialogActions,
  Button,
  Typography
} from '@customink/pigment-react';
import {useHistory} from 'react-router-dom';
import {
  selectPrimaryCampaign,
  selectReplacementCampaign
} from '../../slices/storesSlice';
import {useAppSelector, useCampaignStatus} from '../../hooks';
import {
  useGetStoreDetailsQuery,
  useCampaignPurchaseMutation,
  CampaignPurchaseParamsType
} from '../../api/storesApi';
import {useFetchUserQuery} from '../../api/userApi';
import {selectAdmin} from '../../slices/userSlice';
import './index.scss';

type OrderConfirmDialogProps = {
  storeSlug: string;
};

const OrderConfirmDialog: React.FC<OrderConfirmDialogProps> = ({
  storeSlug
}: OrderConfirmDialogProps): ReactElement => {
  useGetStoreDetailsQuery(storeSlug);
  useFetchUserQuery();
  const storeDetails = useAppSelector(state => state.stores.storeDetails);
  const history = useHistory();
  const campaign = useAppSelector(selectPrimaryCampaign);
  const replacementCampaign = useAppSelector(selectReplacementCampaign);
  const admin = useAppSelector(selectAdmin);
  const {isCampaignCompleted, isCampaignGoalMet, isCampaignCanceled} =
    useCampaignStatus();
  const campaignId = (admin && replacementCampaign?.id) || campaign?.id || null;

  const [
    triggerUseCampaignPurchaseMutation,
    {isSuccess: isPurchaseSuccess, isLoading: isPurchaseLoading}
  ] = useCampaignPurchaseMutation();

  const [open, setOpen] = useState(false);

  const placeOrder = () => {
    const campaignParams: CampaignPurchaseParamsType = {
      url_slug: storeSlug,
      id: campaignId
    };
    triggerUseCampaignPurchaseMutation(campaignParams);
  };

  useEffect(() => {
    if (isPurchaseSuccess && campaignId) {
      history.push(`/${storeSlug}/orderConfirmation/${campaignId}`);
    }
  }, [isPurchaseSuccess, campaign]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const finishedCampaign = isCampaignCanceled || isCampaignCompleted;

  const disableButton =
    !campaign ||
    (!admin && finishedCampaign) ||
    (!admin && !isCampaignGoalMet) ||
    isPurchaseLoading;

  return storeDetails ? (
    <div className="orderConfirmDialog">
      <Button
        className="placeOrderButton"
        disabled={disableButton}
        size="large"
        onClick={handleOpen}>
        Place Order
      </Button>
      <Dialog
        className="orderConfirmDialog-paper"
        title=""
        open={open}
        onClose={handleClose}>
        <DialogContent className="orderConfirmDialog-content">
          <Typography>
            Once you place your order, no additional orders or payments can be
            collected.
            <br />
            Please make sure everyone you expected has already ordered.
          </Typography>
        </DialogContent>
        <DialogActions className="orderConfirmDialog-actions">
          <span className="orderConfirmDialog">
            <Button
              className="placeOrderButton"
              size="large"
              disabled={disableButton}
              onClick={placeOrder}>
              Place Your Order
            </Button>
          </span>
        </DialogActions>
      </Dialog>
    </div>
  ) : null;
};

export default OrderConfirmDialog;
